import PropTypes from 'prop-types';
import { useRef } from 'react';
import { Card } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, GaugeChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import numberHumanizer from '@/helpers/numberHumanizer';
import { getColor, toPersianDigit } from '@/helpers/utils';

echarts.use([
  BarChart,
  GaugeChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  CanvasRenderer
]);

const STATUS_LABELS: Record<string, string> = {
  '2xx': 'موفق (۲xx)',
  '3xx': 'ریدایرکت (۳xx)',
  '4xx': 'خطای ۴xx',
  '5xx': 'خطای ۵xx',
  unknown: 'نامشخص'
};

const STATUS_COLORS: Record<string, string> = {
  '2xx': 'success',
  '3xx': 'warning',
  '4xx': 'warning',
  '5xx': 'danger',
  unknown: 'gray-500'
};

const STATUS_ORDER = ['2xx', '3xx', '4xx', '5xx', 'unknown'];

type NamedValue = { name: string; value: number };

type StatusLegendRow = {
  key: string;
  label: string;
  value: number;
  percent: number;
  color: string;
};

const decodePath = (value: string | null | undefined): string => {
  try {
    return decodeURIComponent(value || '/');
  } catch {
    // Keep malformed legacy percent-encoding visible instead of breaking the chart.
    return value || '/';
  }
};

const formatCount = (value: number) =>
  toPersianDigit(numberHumanizer(value) || '0');

const formatPercent = (value: number) => `${toPersianDigit(String(value))}٪`;

/** Integer percents that still add up to 100 when any row has a value. */
const sharePercents = (values: number[]): number[] => {
  const total = values.reduce((sum, value) => sum + value, 0);
  if (!total) return values.map(() => 0);

  const exact = values.map(value => (value * 100) / total);
  const floors = exact.map(value => Math.floor(value));
  let leftover = 100 - floors.reduce((sum, value) => sum + value, 0);
  const rank = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction);
  const result = [...floors];
  for (let index = 0; index < leftover; index += 1) {
    result[rank[index].index] += 1;
  }
  return result;
};

const toStatusRows = (rows: NamedValue[]): StatusLegendRow[] => {
  const ordered = [...rows].sort((a, b) => {
    const aIndex = STATUS_ORDER.indexOf(a.name);
    const bIndex = STATUS_ORDER.indexOf(b.name);
    return (aIndex === -1 ? STATUS_ORDER.length : aIndex) -
      (bIndex === -1 ? STATUS_ORDER.length : bIndex);
  });
  const percents = sharePercents(ordered.map(row => row.value));

  return ordered.map((row, index) => ({
    key: row.name,
    label: STATUS_LABELS[row.name] || row.name,
    value: row.value,
    percent: percents[index] ?? 0,
    color: getColor(STATUS_COLORS[row.name] || 'gray-500')
  }));
};

const statusOption = (rows: StatusLegendRow[]) => ({
  textStyle: { fontFamily: 'dana' },
  tooltip: {
    trigger: 'item',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100'), fontFamily: 'dana' },
    borderWidth: 1,
    transitionDuration: 0,
    formatter: (params: { name: string; value: number; percent: number }) =>
      `${params.name}<br/>${formatCount(params.value)} نشانی (${formatPercent(
        Math.round(params.percent)
      )})`
  },
  series: [
    {
      type: 'pie',
      radius: ['62%', '88%'],
      center: ['50%', '50%'],
      padAngle: rows.filter(row => row.value > 0).length > 1 ? 2 : 0,
      avoidLabelOverlap: true,
      label: { show: false },
      labelLine: { show: false },
      itemStyle: {
        borderWidth: 2,
        borderColor: getColor('gray-100')
      },
      emphasis: { scale: true, scaleSize: 4 },
      data: rows
        .filter(row => row.value > 0)
        .map(row => ({
          name: row.label,
          value: row.value,
          itemStyle: { color: row.color }
        }))
    }
  ]
});

const pathOption = (rows: NamedValue[]) => ({
  textStyle: { fontFamily: 'dana' },
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    formatter: (params: Array<{ name?: string; value?: number }>) =>
      `${params[0]?.name}<br/>${numberHumanizer(params[0]?.value ?? 0)} بک‌لینک`
  },
  grid: {
    containLabel: true,
    left: '3%',
    right: '5%',
    top: '4%',
    bottom: '4%'
  },
  xAxis: {
    type: 'value',
    axisLabel: { color: getColor('gray-500') },
    splitLine: { lineStyle: { color: getColor('gray-200') } }
  },
  yAxis: {
    type: 'category',
    inverse: true,
    data: rows.map(row => row.name),
    axisLabel: {
      color: getColor('gray-700'),
      width: 150,
      overflow: 'truncate'
    },
    axisTick: { show: false },
    axisLine: { show: false }
  },
  series: [
    {
      type: 'bar',
      data: rows.map(row => row.value),
      barMaxWidth: 22,
      itemStyle: {
        color: getColor('primary'),
        borderRadius: [0, 4, 4, 0]
      }
    }
  ]
});

const toDisplayPathRows = (rows: NamedValue[]) =>
  rows.map(row => ({
    ...row,
    name: decodePath(row.name)
  }));

const ColorSwatch = ({ color }: { color: string }) => (
  <span
    className="rounded-circle flex-shrink-0"
    style={{ width: 8, height: 8, background: color }}
    aria-hidden
  />
);

const StatusCard = ({ rows }: { rows: NamedValue[] }) => {
  const chartRef = useRef<ReactEChartsCore>(null);
  const legend = toStatusRows(rows);
  const total = legend.reduce((sum, row) => sum + row.value, 0);
  const slices = legend.filter(row => row.value > 0);

  const highlight = (label: string) => {
    chartRef.current?.getEchartsInstance()?.dispatchAction({
      type: 'highlight',
      seriesIndex: 0,
      name: label
    });
  };

  const clearHighlight = () => {
    chartRef.current?.getEchartsInstance()?.dispatchAction({
      type: 'downplay',
      seriesIndex: 0
    });
  };

  return (
    <Card className="h-100">
      <FalconCardHeader
        title={
          <div className="d-flex align-items-center gap-2">
            <span>وضعیت نشانی‌های پیوند</span>
            <HelpTooltip html="تعداد نشانی‌های مقصد یکتا را بر اساس آخرین وضعیت HTTP نشان می‌دهد؛ ۲xx موفق، ۳xx ریدایرکت، ۴xx و ۵xx خطا و نامشخص یعنی بدون کد معتبر." />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
      />
      <Card.Body className="d-flex flex-column justify-content-center">
        {total === 0 ? (
          <div className="text-center text-600 fs--1 py-4">
            داده‌ای برای نمایش نیست
          </div>
        ) : (
          <>
            <div
              className="position-relative mx-auto"
              style={{ width: 132, height: 132 }}
            >
              <ReactEChartsCore
                ref={chartRef}
                echarts={echarts}
                option={statusOption(legend)}
                style={{ width: 132, height: 132 }}
                notMerge
              />
              <div className="position-absolute top-50 start-50 translate-middle text-center pe-none">
                <div className="fw-bold fs-1 lh-1">{formatCount(total)}</div>
                <div className="text-600 fs--2 mt-1">نشانی</div>
              </div>
            </div>
            {/* HTML legend: an ECharts legend indents labels beside the donut in RTL. */}
            <ul className="list-unstyled mb-0 mt-2">
              {legend.map(row => (
                <li
                  key={row.key}
                  className="d-flex align-items-center gap-2 py-1 rounded-2 hover-bg-200"
                  onMouseEnter={() => slices.length && highlight(row.label)}
                  onMouseLeave={clearHighlight}
                >
                  <ColorSwatch color={row.color} />
                  <span className="text-truncate fs--1">{row.label}</span>
                  <span className="ms-auto text-nowrap fs--1">
                    <span className="fw-semi-bold">{formatCount(row.value)}</span>
                    <span className="text-600"> ({formatPercent(row.percent)})</span>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const followGaugeOption = (value: number, follow: number, nofollow: number) => ({
  textStyle: { fontFamily: 'dana' },
  animationDuration: 400,
  tooltip: {
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100'), fontFamily: 'dana' },
    borderWidth: 1,
    transitionDuration: 0,
    formatter: () =>
      `فالو: ${formatCount(follow)}<br/>نوفالو: ${formatCount(nofollow)}`
  },
  series: [
    {
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      min: 0,
      max: 100,
      radius: '90%',
      center: ['50%', '50%'],
      pointer: { show: false },
      anchor: { show: false },
      progress: {
        show: true,
        roundCap: true,
        width: 18,
        itemStyle: { color: getColor('primary') }
      },
      axisLine: {
        roundCap: true,
        lineStyle: {
          width: 18,
          color: [[1, getColor('gray-400')]]
        }
      },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      detail: { show: false },
      title: { show: false },
      data: [{ value }]
    }
  ]
});

const FollowNoFollowCard = ({ data }: Record<string, any>) => {
  const follow = data?.follow ?? 0;
  const nofollow = data?.nofollow ?? 0;
  const total = data?.total ?? follow + nofollow;
  const followPercentage =
    data?.follow_percentage ?? (total ? Math.round((follow * 100) / total) : 0);
  const nofollowPercentage =
    data?.nofollow_percentage ??
    (total ? Math.round((nofollow * 100) / total) : 0);
  const arc = total ? (follow / total) * 100 : 0;
  const followColor = getColor('primary');
  const nofollowColor = getColor('gray-400');
  const rows = [
    { label: 'فالو', count: follow, percentage: followPercentage, color: followColor },
    {
      label: 'نوفالو',
      count: nofollow,
      percentage: nofollowPercentage,
      color: nofollowColor
    }
  ];

  return (
    <Card className="h-100">
      <FalconCardHeader
        title={
          <div className="d-flex align-items-center gap-2">
            <span>فالو و نوفالو</span>
            <HelpTooltip html="تعداد و درصد بک‌لینک‌های فالو و نوفالو از کل بک‌لینک‌های این نشانی را نشان می‌دهد." />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
      />
      <Card.Body className="d-flex flex-column justify-content-center">
        {total === 0 ? (
          <div className="text-center text-600 fs--1 py-4">
            داده‌ای برای نمایش نیست
          </div>
        ) : (
          <>
            <div
              className="position-relative w-100 overflow-hidden"
              style={{ aspectRatio: '1.7 / 1' }}
            >
              <ReactEChartsCore
                echarts={echarts}
                option={followGaugeOption(arc, follow, nofollow)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '200%'
                }}
                notMerge
              />
              <div
                className="position-absolute start-50 translate-middle-x text-center pe-none"
                style={{ bottom: 0 }}
              >
                <div className="fw-bold fs-2 lh-1">
                  {formatPercent(followPercentage)}
                </div>
                <div className="text-600 fs--2 mt-1">سهم فالو</div>
              </div>
            </div>
            <div className="d-flex mt-3 pt-2 border-top">
              {rows.map((row, index) => (
                <div
                  key={row.label}
                  className={`flex-fill text-center ${index === 0 ? 'border-end' : ''}`}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2 text-600 fs--1">
                    <ColorSwatch color={row.color} />
                    <span>{row.label}</span>
                  </div>
                  <div className="fw-bold mt-1">{formatCount(row.count)}</div>
                  <div className="text-600 fs--2">
                    {formatPercent(row.percentage)}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const InternalPageCharts = ({ data }: Record<string, any>) => {
  const statusRows = data?.status ?? [];
  const pathRows = toDisplayPathRows(data?.paths ?? []);

  return (
    <div className="row g-3 mb-3">
      <div className="col-12 col-lg-3">
        <StatusCard rows={statusRows} />
      </div>
      <div className="col-12 col-lg-3">
        <FollowNoFollowCard data={data?.follow} />
      </div>
      <div className="col-12 col-lg-6">
        <Card className="h-100">
          <FalconCardHeader
            title={
              <div className="d-flex align-items-center gap-2">
                <span>پراکندگی بر اساس اولین path</span>
                <HelpTooltip html="بخش اول مسیر نشانی‌های مقصد را گروه‌بندی می‌کند. هر میله تعداد بک‌لینک‌های آن مسیر را نشان می‌دهد و فقط ۱۰ مسیر پرتکرار نمایش داده می‌شود." />
              </div>
            }
            titleTag="h6"
            className="py-2"
            light
            endEl={<span className="text-600 fs--1">۱۰ مسیر پرتکرار</span>}
          />
          <Card.Body className="p-1">
            <ReactEChartsCore
              echarts={echarts}
              option={pathOption(pathRows)}
              style={{ height: 250 }}
              notMerge
            />
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

FollowNoFollowCard.propTypes = {
  data: PropTypes.shape({
    follow: PropTypes.number,
    nofollow: PropTypes.number,
    total: PropTypes.number,
    follow_percentage: PropTypes.number,
    nofollow_percentage: PropTypes.number
  })
};

InternalPageCharts.propTypes = {
  data: PropTypes.shape({
    status: PropTypes.array,
    follow: PropTypes.object,
    paths: PropTypes.array
  })
};

export default InternalPageCharts;
